import { type Ref } from 'vue'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { useRenderLoop, type RenderContext } from '@/composables/useRenderLoop'
import { useCameraControl, type Viewport } from '@/composables/useCameraControl'
import { useTransformGizmo, type GizmoMode } from '@/composables/useTransformGizmo'
import { applySceneEnvironment, type SceneEnvironment } from '@/utils/sceneEnvironment'
import { enableAcceleratedPicking } from '@/utils/meshPicking'

export type { Viewport, GizmoMode }

const BACKGROUND = 0xf0f0f0

export function useThreeScene(container: Ref<HTMLDivElement | undefined>) {
  let renderer: THREE.WebGLRenderer | null = null
  let scene: THREE.Scene | null = null
  let camera: THREE.PerspectiveCamera | null = null
  let controls: OrbitControls | null = null
  let environment: SceneEnvironment | null = null
  let resizeObserver: ResizeObserver | null = null

  const getScene = () => scene
  const getCamera = () => camera
  const getControls = () => controls

  function getContext(): RenderContext | null {
    return renderer && scene && camera ? { renderer, scene, camera } : null
  }

  const loop = useRenderLoop(getContext)
  const cameraControl = useCameraControl(getCamera, getControls, loop)
  const gizmo = useTransformGizmo(loop)

  function initRenderer() {
    if (!container.value) return
    enableAcceleratedPicking()

    scene = new THREE.Scene()
    scene.background = new THREE.Color(BACKGROUND)
    // World matrices are driven by the render loop instead, so an idle model does no matrix work.
    scene.matrixWorldAutoUpdate = false

    const w = container.value.clientWidth
    const h = container.value.clientHeight
    camera = new THREE.PerspectiveCamera(45, w / h, 0.001, 100000)
    camera.position.set(0, 0, 5)

    const canvas = document.createElement('canvas')
    canvas.setAttribute('data-openreplay-obscured', '')
    canvas.setAttribute('data-sentry-block', '')
    renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance',
    })
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.setSize(w, h)
    container.value.appendChild(renderer.domElement)

    environment = applySceneEnvironment(scene)

    controls = new OrbitControls(camera, renderer.domElement)
    controls.enableDamping = true
    controls.dampingFactor = 0.05
    controls.addEventListener('start', () => loop.setInteracting(true))
    controls.addEventListener('end', () => loop.setInteracting(false))
    controls.addEventListener('change', () => loop.requestRender())
    loop.addFrameTask(() => controls?.update() ?? false)

    gizmo.init(camera, renderer.domElement, scene, controls)

    resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(container.value)
    loop.markSceneChanged()
  }

  function handleResize() {
    if (!container.value || !camera || !renderer) return
    const w = container.value.clientWidth
    const h = container.value.clientHeight
    if (w === 0 || h === 0) return
    camera.aspect = w / h
    camera.updateProjectionMatrix()
    renderer.setSize(w, h)
    // Entering fullscreen changes what resolution the canvas can afford, so the budget is re-evaluated.
    loop.refreshPixelRatio()
    loop.renderNow()
  }

  function disposeRenderer() {
    loop.dispose()
    resizeObserver?.disconnect()
    resizeObserver = null
    gizmo.dispose()
    controls?.dispose()
    environment?.dispose()
    if (renderer) {
      if (renderer.domElement.parentNode === container.value) {
        container.value?.removeChild(renderer.domElement)
      }
      renderer.dispose()
      renderer.forceContextLoss()
    }
    renderer = null
    scene = null
    camera = null
    controls = null
    environment = null
  }

  async function precompile(objects: THREE.Object3D[]): Promise<void> {
    if (!renderer || !scene || !camera || objects.length === 0) return
    const holder = new THREE.Group()
    holder.add(...objects)
    await renderer.compileAsync(holder, camera, scene)
    holder.clear()
  }

  return {
    getScene,
    getCamera,
    precompile,
    initRenderer,
    disposeRenderer,

    stats: loop.stats,
    markSceneChanged: loop.markSceneChanged,
    flushWorldMatrices: loop.flushWorldMatrices,
    addFrameTask: loop.addFrameTask,
    addWorldFlushTask: loop.addWorldFlushTask,

    fitCamera: cameraControl.fitCamera,
    resetCamera: cameraControl.resetCamera,
    applyViewport: cameraControl.applyViewport,
    refitToBox: cameraControl.refitToBox,
    focusBox: cameraControl.focusBox,
    currentViewport: cameraControl.currentViewport,

    attachGizmo: gizmo.attach,
    detachGizmo: gizmo.detach,
    onGizmoChange: gizmo.setOnChange,
    isGizmoDragging: gizmo.isDragging,
  }
}
